import React, { useEffect, useContext, useState } from 'react'
import { DesignContext } from 'contexts/DesignContext'
import { HistoryContext } from 'contexts/HistoryContext'
import { useRouteMatch, useLocation } from 'react-router-dom'
import objectPath from 'object-path'
import PageLayout from 'designer/component/display/layout/PageLayout'

// Action
import { getLocalPage } from 'designer/component/action/LocalDesign'
import {
  getDesign,
  getPage,
  getWidgetDesign,
  loadDesignerData,
  loadUrlParamToFormData,
  resetJsonForm,
} from 'designer/component/action/Design'
import { pageSchema, pageUischema } from 'designer/component/action/Schema'
import FormLoading from 'components/Loader/FormLoading'
import {
  onCheckProviderCondition,
} from 'designer/component/action/condition/Action'
import {
  checkRulesOrCondition,
} from 'designer/component/action/CheckRulesOrCondition'
import { useLockEdit } from 'contexts/PatientLockEditProvider'
import useAuth from 'hooks/useAuth'
import ModalCondition from 'designer/component/component/ModalCondition'
import { useTimeFormat } from 'contexts/TimeFormatContext'
import { createWidgetOptions } from 'designer/component/action/Option'
import { createWidgetDesign } from 'designer/component/action/Widget'
import { createDropdown } from 'designer/component/action/Dropdown'
import {
  getFormPageReadyKey,
  setFormPageReady,
} from 'designer/component/action/FormPageReady'

const Page = (props) => {
  const {
    designerData = {},
    onUpdateData = () => {},
    onSelectRow = () => {},
    onCloseModal = () => {},
    uischema,
    data = null,
    callback = {},
    designCode,
    isOpenModal,
    isModalCondition,
  } = props
  const match = useRouteMatch()
  const location = useLocation()
  let search = location.search || []
  let pathParams = match.params
  let urlSearchParams = new URLSearchParams(search)
  let queryParams = Object.fromEntries([...urlSearchParams])
  const urlParams = { ...pathParams, ...queryParams }
  let perspectiveRoute = match.params.perspective || ''
  let queryString = JSON.stringify(queryParams)
  const formPageReadyKey = getFormPageReadyKey(location)

  const historyContext = useContext(HistoryContext)
  const { historyState } = historyContext
  const patientLock = useLockEdit()
  const auth = useAuth()
  const { user } = auth
  const designContext = useContext(DesignContext)
  const {
    jsonForm,
    resetContext,
    setComponentPropsData,
    searchData,
    isUiChanged,
    setIsUiChanged,
    setDatasourceValue,
    setFormData,
    isInputSearch,
    setIsInputSearch,
    setAllFormGroup,
    widgetDesign,
    getEmployee,
    setSearchData,
    setWidgetDesign,
  } = designContext
  let isFormReady = objectPath.get(isUiChanged, 'modal.isFormReady')
  let datasourceValue = objectPath.get(designContext, '@datasource') || {}
  let designPageCode = designContext?.formDesign?.code
  let currentCollapse = localStorage.getItem('currentCollapse')

  const componentPropsData = jsonForm?.componentPropsData || {}
  const componentData = data || objectPath.get(componentPropsData, 'data')
  const { isClosed, isSelectedRow } = componentPropsData
  const [isLoadPage, setIsLoadPage] = useState(false)
  const [isCheckedCondition, setIsCheckedCondition] = useState(false)
  const [isWidgetReady, setIsWidgetReady] = useState(false)
  const [code, setCode] = useState('')
  const pageCode =
    designCode ||
    uischema?.ui?.formDesign?.code ||
    designerData?.pageCode ||
    match.params.pageCode ||
    ''
  const isChangePage = !isOpenModal && pageCode !== designPageCode
  const modalCode = objectPath.get(uischema, 'ui.jsonForm.formCode')
  const {
    formatDate,
    formatTime,
    formatDateTime,
    dateFormat,
    timeFormat,
  } = useTimeFormat()

  useEffect(() => {
    if (isOpenModal) return

    setFormPageReady({
      readyKey: formPageReadyKey,
      isLoading: true,
      source: 'page',
    })

    return () => {
      setFormPageReady({
        readyKey: formPageReadyKey,
        isLoading: false,
        source: 'page',
      })
    }
  }, [formPageReadyKey, isOpenModal])

  useEffect(() => {
    if (isOpenModal) return
    if (!designPageCode || isLoadPage || !isWidgetReady) return

    setFormPageReady({
      readyKey: formPageReadyKey,
      isReady: true,
      isLoading: false,
      source: 'page',
    })
  }, [formPageReadyKey, isOpenModal, designPageCode, isLoadPage, isWidgetReady])

  useEffect(() => {
    if (!pageCode) return
    if (pageCode !== code) {
      setCode(pageCode)
    }
    if (jsonForm.currentCollapse !== currentCollapse) {
      setCode(pageCode)
    }

    return () => {
      if (jsonForm.currentCollapse) {
        resetContext(true)
        setCode('')
      }
      if (!isOpenModal && pathParams.pageCode === pageCode) {
        resetContext(true)
        objectPath.set(isUiChanged, 'modal.isFormReady', false)
      }
      objectPath.set(isUiChanged, 'tempOption', {})
      objectPath.set(isUiChanged, 'optionsTemp', {})
      setIsUiChanged(isUiChanged)
    }
  }, [pageCode, currentCollapse])

  useEffect(async () => {
    if (!code) return
    if (isOpenModal && isFormReady && jsonForm.formCode) return
    setSearchData({})
    objectPath.del(isUiChanged, 'fetchSearch')
    setIsUiChanged(isUiChanged)
    objectPath.set(datasourceValue, `pathParam`, pathParams)
    objectPath.set(datasourceValue, `queryParam`, queryParams)
    setIsCheckedCondition(false)
    if (location.pathname === historyState?.pathname) {
      location.state = historyState.state
    }
    if (
      objectPath.get(queryParams, 'previewExample') === '1' &&
      localStorage.getItem(pageCode)
    ) {
      getLocalPage(pageCode, perspectiveRoute, resetContext)
    } else {
      setIsLoadPage(true)
      let { jsonForm, formDesign, searchData, datasource } = await getDesign(
        pageCode,
        {
          pathParams,
          queryParams,
          location,
          designerData,
          datasourceValue,
          uischema,
          isPage: true,
        },
      )
      resetJsonForm({
        datasourceValue,
        auth,
        user: user,
        isPage: 1,
        designCode: pageCode,
        formDesign: formDesign,
        jsonForm: { ...jsonForm, isPage: 1 },
        perspectiveRoute: perspectiveRoute,
        resetContext: resetContext,
        uischema: pageUischema,
        schema: pageSchema,
        searchData: searchData,
        isSearch: true,
        elementAction: {},
        datasource: datasource,
        callback: callback,
        formatDate: formatDate,
        formatTime: formatTime,
        formatDateTime: formatDateTime,
        dateFormat: dateFormat,
        timeFormat: timeFormat,
        urlParams: urlParams,
        isOpenModal,
        isImmediateReset: true,
      })
    }

    if (uischema) {
      objectPath.set(isUiChanged, 'modal.isFormReady', true)
      setIsUiChanged(isUiChanged)
    }
    setIsLoadPage(false)
  }, [code, currentCollapse])

  useEffect(() => {
    if (isClosed && onSelectRow && isSelectedRow) {
      let receiveProps = objectPath.get(uischema, 'ui.receiveProps')
      let isCustomComponent = objectPath.get(uischema, 'isCustomComponent')
      if (isCustomComponent) {
        let rowData = objectPath.get(componentPropsData, 'rowData')
        onSelectRow(rowData, isClosed, receiveProps)
        objectPath.set(componentPropsData, 'isSelectedRow', false)
        setComponentPropsData(componentPropsData)
      } else {
        let search = objectPath.get(jsonForm, 'search.field') || []
        search.forEach(({ key, name, hasElement }) => {
          if (hasElement) {
            let value = objectPath.get(searchData, key)
            objectPath.set(data, name, value)
          }
        })
        objectPath.set(componentPropsData, 'data', componentData)
        objectPath.set(componentPropsData, 'isSelectedRow', false)
        let isExternal = designerData?.pageCode ? true : false
        onSelectRow(componentData, isClosed, receiveProps, isExternal)
        setComponentPropsData(componentPropsData)
      }
    } else if (isClosed) {
      if (onUpdateData) {
        onUpdateData(componentPropsData)
      }
      if (onCloseModal) {
        onCloseModal(componentPropsData)
      }
    }
  }, [isClosed, isSelectedRow])

  useEffect(async () => {
    if (designPageCode) {
      setIsWidgetReady(false)
      createDropdown(designContext)
      await createWidgetDesign(designContext)
      setIsWidgetReady(true)
    }
  }, [designPageCode])

  useEffect(() => {
    if (!isOpenModal && urlParams?.pageCode === jsonForm?.formCode) {
      let formData = objectPath.get(jsonForm, 'formData')
      formData = loadUrlParamToFormData(
        jsonForm,
        formData,
        pathParams,
        queryParams,
        datasourceValue,
      )
      objectPath.set(datasourceValue, 'queryParam', queryParams)
      objectPath.set(designContext, '@datasource', datasourceValue)
      objectPath.set(designContext, 'jsonForm.formData', formData)
      setDatasourceValue(datasourceValue)
      setFormData(formData)
      checkRulesOrCondition(designContext, formData, null, {
        designCode: designPageCode,
        queryString: queryString,
      })
    }
  }, [designPageCode, location])

  useEffect(() => {
    if (isOpenModal && designPageCode) {
      let formData = objectPath.get(jsonForm, 'formData')
      formData = loadUrlParamToFormData(
        jsonForm,
        formData,
        pathParams,
        queryParams,
        datasourceValue,
      )
      objectPath.set(datasourceValue, 'queryParam', queryParams)
      objectPath.set(designContext, '@datasource', datasourceValue)
      objectPath.set(designContext, 'jsonForm.formData', formData)
      setDatasourceValue(datasourceValue)
      setFormData(formData)
      let modalCondition = jsonForm.conditions
      checkRulesOrCondition(designContext, formData, null, {
        designCode: designPageCode,
        isModalCondition: isModalCondition,
        modalCondition: modalCondition,
        queryString: queryString,
        isOpenModal,
      })
    }
  }, [designPageCode && isOpenModal])

  useEffect(() => {
    if (isInputSearch) {
      setIsInputSearch(false)
      objectPath.set(isUiChanged, 'fetchSearch', searchData)
      setIsUiChanged(isUiChanged)
    }
  }, [isInputSearch])

  useEffect(() => {
    if (patientLock) {
      onCheckProviderCondition(designContext, {
        Patient: patientLock,
      })
    }
  }, [designPageCode && patientLock])

  if (isLoadPage || isChangePage) {
    return <FormLoading />
  }

  return (
    <>
      <PageLayout
        isOpenModal={isOpenModal}
        schema={jsonForm?.schema || {}}
        uischema={jsonForm?.uischema || {}}
      />
      <ModalCondition />
    </>
  )
}

export default Page
